// Invoices as stored in Firestore (users/{uid}/invoices/{id}), with their
// figures and status worked out the same way as the phone app: a port of
// packages/paidlark_core/lib/data/models/invoice.dart. Status is derived,
// never stored; money is whole minor units.

import { addDays, compareStamps, daysBetween, type IsoDate, toIsoDate } from '@/core/dates';
import { type Currency, currencyOf, formatBasisPoints, Money } from '@/core/money';

export type InvoiceStatus = 'draft' | 'pending' | 'partiallyPaid' | 'paid' | 'overdue';
export type PaymentMethod = 'bankTransfer' | 'cash' | 'pos' | 'onlineLink' | 'other';
export const PAYMENT_METHODS: readonly PaymentMethod[] = ['bankTransfer', 'cash', 'pos', 'onlineLink', 'other'];

export interface LineItem {
  id: string;
  name: string;
  description: string;
  /** May be fractional (1.5 hours); two decimals count. */
  quantity: number;
  /** In the invoice's currency. */
  rateMinor: number;
}

export interface Payment {
  id: string;
  amountMinor: number;
  /** The calendar date the money arrived. */
  date: IsoDate;
  method: PaymentMethod;
  note: string;
}

export type Discount =
  | { type: 'none'; basisPoints: 0; amountMinor: 0 }
  | { type: 'percent'; basisPoints: number; amountMinor: 0 }
  | { type: 'amount'; basisPoints: 0; amountMinor: number };

export const NO_DISCOUNT: Discount = { type: 'none', basisPoints: 0, amountMinor: 0 };

export interface TaxRate {
  name: string;
  /** 750 = 7.5%. */
  basisPoints: number;
}

export const NO_TAX: TaxRate = { name: '', basisPoints: 0 };
export const NIGERIA_VAT: TaxRate = { name: 'VAT', basisPoints: 750 };

/** "VAT (7.5%)". */
export const taxLabel = (tax: TaxRate) => `${tax.name} (${formatBasisPoints(tax.basisPoints)})`;

/** The client's details as they were when the invoice was made. */
export interface ClientSnapshot {
  name: string;
  email: string;
  phone: string;
  address: string;
}

export interface InvoiceDesign {
  templateId: string;
  /** ARGB, as the phone app stores it. */
  brandColor: number;
  fontId: string;
  showLogo: boolean;
  showTax: boolean;
  showSignature: boolean;
  /** "Use INV-0001 as your payment reference". Off unless turned on. */
  showPaymentReference: boolean;
  /** "Made with Paidlark". Always shown on the free plan. */
  showPaidlarkLine: boolean;
}

export const DEFAULT_DESIGN: InvoiceDesign = {
  templateId: 'classic',
  brandColor: 0xff0e5e4e,
  fontId: 'jakarta',
  showLogo: true,
  showTax: true,
  showSignature: false,
  showPaymentReference: false,
  showPaidlarkLine: true,
};

export interface Invoice {
  id: string;
  businessId: string;
  /** e.g. INV-0143. */
  number: string;
  /** Null when the client was typed in without being saved. */
  clientId: string | null;
  client: ClientSnapshot;
  /** ISO 4217 code. */
  currency: string;
  issueDate: IsoDate;
  dueDate: IsoDate;
  items: LineItem[];
  discount: Discount;
  tax: TaxRate;
  notes: string;
  payments: Payment[];
  design: InvoiceDesign;
  /** When first shared or sent; null means a draft. */
  sentAt: string | null;
  /** The public page (publicInvoices/{id}) once a link has been shared. */
  publicLinkId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface InvoiceTotals {
  subtotal: Money;
  discount: Money;
  taxable: Money;
  tax: Money;
  total: Money;
  paid: Money;
  /** Still owed; never negative. */
  balance: Money;
}

// ── Reading and writing ──────────────────────────────────────────────────

type Json = Record<string, unknown>;
const str = (v: unknown, fallback = ''): string => (typeof v === 'string' ? v : fallback);
const num = (v: unknown, fallback = 0): number => (typeof v === 'number' && Number.isFinite(v) ? v : fallback);
const bool = (v: unknown, fallback: boolean): boolean => (typeof v === 'boolean' ? v : fallback);
const obj = (v: unknown): Json | null => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Json) : null);
const list = (v: unknown): Json[] => (Array.isArray(v) ? v.map(obj).filter((x): x is Json => x !== null) : []);

export function discountFromJson(json: Json | null): Discount {
  switch (json?.type) {
    case 'percent':
      return { type: 'percent', basisPoints: Math.trunc(num(json.basisPoints)), amountMinor: 0 };
    case 'amount':
      return { type: 'amount', basisPoints: 0, amountMinor: Math.trunc(num(json.amountMinor)) };
    default:
      return NO_DISCOUNT;
  }
}

export function taxFromJson(json: Json | null): TaxRate {
  return json ? { name: str(json.name), basisPoints: Math.trunc(num(json.basisPoints)) } : NO_TAX;
}

export function snapshotFromJson(json: Json | null): ClientSnapshot {
  return { name: str(json?.name), email: str(json?.email), phone: str(json?.phone), address: str(json?.address) };
}

export function designFromJson(json: Json | null): InvoiceDesign {
  if (!json) return DEFAULT_DESIGN;
  return {
    templateId: str(json.templateId, 'classic'),
    brandColor: Math.trunc(num(json.brandColor, DEFAULT_DESIGN.brandColor)),
    fontId: str(json.fontId, 'jakarta'),
    showLogo: bool(json.showLogo, true),
    showTax: bool(json.showTax, true),
    showSignature: bool(json.showSignature, false),
    showPaymentReference: bool(json.showPaymentReference, false),
    showPaidlarkLine: bool(json.showPaidlarkLine, true),
  };
}

function methodOf(v: unknown): PaymentMethod {
  return PAYMENT_METHODS.find((m) => m === v) ?? 'other';
}

/** Reads a stored invoice; throws when it has no id or dates. */
export function invoiceFromJson(json: Json): Invoice {
  const id = str(json.id);
  if (!id || typeof json.issueDate !== 'string' || typeof json.dueDate !== 'string') throw new Error('Not an invoice');
  return {
    id,
    businessId: str(json.businessId),
    number: str(json.number),
    clientId: typeof json.clientId === 'string' ? json.clientId : null,
    client: snapshotFromJson(obj(json.client)),
    currency: currencyOf(str(json.currency, 'NGN')).code,
    issueDate: toIsoDate(json.issueDate),
    dueDate: toIsoDate(json.dueDate),
    items: list(json.items).map((i) => ({
      id: str(i.id),
      name: str(i.name),
      description: str(i.description),
      quantity: num(i.quantity, 1),
      rateMinor: Math.trunc(num(i.rateMinor)),
    })),
    discount: discountFromJson(obj(json.discount)),
    tax: taxFromJson(obj(json.tax)),
    notes: str(json.notes),
    payments: list(json.payments).map((p) => ({
      id: str(p.id),
      amountMinor: Math.trunc(num(p.amountMinor)),
      date: toIsoDate(str(p.date)),
      method: methodOf(p.method),
      note: str(p.note),
    })),
    design: designFromJson(obj(json.design)),
    sentAt: typeof json.sentAt === 'string' ? json.sentAt : null,
    publicLinkId: typeof json.publicLinkId === 'string' ? json.publicLinkId : null,
    createdAt: str(json.createdAt),
    updatedAt: str(json.updatedAt),
  };
}

/** The stored form, field for field what the phone app writes. */
export function invoiceToJson(invoice: Invoice): Json {
  const t = totalsOf(invoice);
  return {
    id: invoice.id,
    businessId: invoice.businessId,
    number: invoice.number,
    clientId: invoice.clientId,
    client: { ...invoice.client },
    currency: invoice.currency,
    issueDate: invoice.issueDate,
    dueDate: invoice.dueDate,
    items: invoice.items.map((i) => ({ id: i.id, name: i.name, description: i.description, quantity: i.quantity, rateMinor: i.rateMinor })),
    discount: { ...invoice.discount },
    tax: { ...invoice.tax },
    notes: invoice.notes,
    payments: invoice.payments.map((p) => ({ id: p.id, amountMinor: p.amountMinor, date: p.date, method: p.method, note: p.note })),
    design: { ...invoice.design },
    sentAt: invoice.sentAt,
    publicLinkId: invoice.publicLinkId,
    createdAt: invoice.createdAt,
    updatedAt: invoice.updatedAt,
    // Stored for queries and reports; always worked out again on read.
    totalMinor: t.total.minor,
    balanceMinor: t.balance.minor,
  };
}

// ── Figures and status ───────────────────────────────────────────────────

export const currencyOfInvoice = (invoice: Invoice): Currency => currencyOf(invoice.currency);

export function itemAmount(item: LineItem, currency: Currency): Money {
  return new Money(item.rateMinor, currency).times(item.quantity);
}

/** The discount on [subtotal], never more than the subtotal. */
export function discountOn(discount: Discount, subtotal: Money): Money {
  switch (discount.type) {
    case 'none':
      return Money.zero(subtotal.currency);
    case 'percent':
      return subtotal.percent(discount.basisPoints).min(subtotal);
    case 'amount':
      return new Money(discount.amountMinor, subtotal.currency).min(subtotal);
  }
}

// Invoices are replaced, never changed in place, so each one's totals are
// worked out once.
const totalsCache = new WeakMap<Invoice, InvoiceTotals>();

export function totalsOf(invoice: Invoice): InvoiceTotals {
  const cached = totalsCache.get(invoice);
  if (cached) return cached;
  const currency = currencyOfInvoice(invoice);
  const zero = Money.zero(currency);
  const subtotal = invoice.items.reduce((sum, item) => sum.plus(itemAmount(item, currency)), zero);
  const discount = discountOn(invoice.discount, subtotal);
  const taxable = subtotal.minus(discount);
  const tax = invoice.tax.basisPoints === 0 ? zero : taxable.percent(invoice.tax.basisPoints);
  const total = taxable.plus(tax);
  const paid = invoice.payments.reduce((sum, p) => sum.plus(new Money(p.amountMinor, currency)), zero);
  const balance = total.minus(paid).max(zero);
  const totals = { subtotal, discount, taxable, tax, total, paid, balance };
  totalsCache.set(invoice, totals);
  return totals;
}

export const isDraft = (invoice: Invoice) => invoice.sentAt === null;

/** Status on the calendar date [today]. */
export function statusOn(invoice: Invoice, today: IsoDate): InvoiceStatus {
  if (isDraft(invoice)) return 'draft';
  const t = totalsOf(invoice);
  if (t.total.isPositive && t.balance.isZero) return 'paid';
  if (daysBetween(invoice.dueDate, today) > 0) return 'overdue';
  if (t.paid.isPositive) return 'partiallyPaid';
  return 'pending';
}

/** Days past the due date; 0 when not overdue. */
export function daysLate(invoice: Invoice, today: IsoDate): number {
  return Math.max(0, daysBetween(invoice.dueDate, today));
}

/** Newest issue date first; the same day, newest created first. */
export function sortInvoices(invoices: Invoice[]): Invoice[] {
  return [...invoices].sort((a, b) => compareStamps(b.issueDate, a.issueDate) || compareStamps(b.createdAt, a.createdAt));
}

/**
 * A new draft with the same client, items and settings, dated [today] with
 * the same payment terms. Items get fresh ids; payments, sending and the
 * public page stay with the original.
 */
export function duplicateInvoice(invoice: Invoice, args: { id: string; number: string; today: IsoDate; now: string; ids: () => string }): Invoice {
  return {
    ...invoice,
    id: args.id,
    number: args.number,
    issueDate: args.today,
    dueDate: addDays(args.today, daysBetween(invoice.issueDate, invoice.dueDate)),
    items: invoice.items.map((i) => ({ ...i, id: args.ids() })),
    payments: [],
    sentAt: null,
    publicLinkId: null,
    createdAt: args.now,
    updatedAt: args.now,
  };
}

/** Up to two initials for an avatar ("Greenfield Farms" → "GF"), or "?". */
export function initialsOf(name: string): string {
  const words = name.split(/\s+/).filter((w) => w.length > 0 && /[A-Za-z0-9]/.test(w[0]));
  const letters = words
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');
  return letters || '?';
}

// ── The list's tabs and search ───────────────────────────────────────────

export type InvoiceFilter = 'all' | 'draft' | 'pending' | 'overdue' | 'paid';
export const INVOICE_FILTERS: readonly InvoiceFilter[] = ['all', 'draft', 'pending', 'overdue', 'paid'];

export function filterMatches(filter: InvoiceFilter, status: InvoiceStatus): boolean {
  switch (filter) {
    case 'all':
      return true;
    case 'draft':
      return status === 'draft';
    // Part-paid invoices are still waiting for money.
    case 'pending':
      return status === 'pending' || status === 'partiallyPaid';
    case 'overdue':
      return status === 'overdue';
    case 'paid':
      return status === 'paid';
  }
}

/** Client name, invoice number or amount ("485" finds ₦485,000). */
export function invoiceMatchesQuery(invoice: Invoice, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  if (invoice.client.name.toLowerCase().includes(q) || invoice.number.toLowerCase().includes(q)) return true;
  const digits = q.replace(/[^0-9]/g, '');
  return digits.length > 0 && totalsOf(invoice).total.format({ withSymbol: false }).replace(/,/g, '').includes(digits);
}
